import FactoryOutlinedIcon from "@mui/icons-material/FactoryOutlined";
import { Collapse, styled, useTheme } from "@mui/material";
import { lazy, Suspense, useEffect, useState } from "react";
import { SubTitle } from "../SideMenuTree/SubTitle";
import { MenuTreeItemProps } from "./types";
import { Theme } from "../Theme/types";
import MenuItemChildrens from "./MenuItemChildrens";
import { Loading } from "../Loading";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";

const MenuItem = lazy(() => import("./MenuTreeItem"));
export const MenuItemComponent = ({
  item,
  onClickItemHandler,
  depth = 1,
  variant = "default",
  activeItemId,
  customMenuItemComponent,
  openedItemsIds = [],
  ...props
}: MenuTreeItemProps) => {
  const theme = useTheme<Theme>();
  const [isOpen, setIsOpen] = useState(openedItemsIds.includes(item.id));

  useEffect(() => {
    setIsOpen(openedItemsIds.includes(item.id));
  }, [openedItemsIds]);

  const ItemComponent = customMenuItemComponent ?? MenuItem;
  const isActive = activeItemId === item.id;
  const hasChildren =
    (item.children && item.children.length > 0) || item.has_subitems;

  if (variant === "expanded" && depth === 0) {
    return (
      <>
        <SubTitle
          icon={<FactoryOutlinedIcon />}
          text={item.name}
          onClickHandler={() => onClickItemHandler?.(item)}
          className={variant}
        />
        <MenuItemChildrens
          item={item}
          onClickItemHandler={onClickItemHandler}
          depth={depth + 1}
          variant={variant}
          activeItemId={activeItemId}
          isOpen={isOpen}
          openedItemsIds={openedItemsIds}
          setIsOpen={setIsOpen}
          {...props}
        />
      </>
    );
  }
  return (
    <StyledItemWrapper
      className={`level-${depth > 4 ? 4 : depth} ${isActive ? "active" : ""}`}
    >
      <StyledMenuItem
        className={`wrapper level-${depth > 4 ? 4 : depth} ${
          isActive ? "active" : ""
        }`}
        isExpanded={variant === "expanded"}
        onClick={() => onClickItemHandler?.(item)}
        theme={theme}
        sx={{
          paddingLeft: depth >= 1 ? `${8 * (depth + 3)}px` : "",
        }}
      >
        <ItemComponent
          item={item}
          depth={depth}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          {...props}
        />
        {hasChildren && (
          <span
            className="icon-collapse"
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(!isOpen);
            }}
          >
            {isOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </span>
        )}
      </StyledMenuItem>
      {isOpen && (
        <Suspense fallback={<Loading center />}>
          {item.children &&
            item.children.map((child) => (
              <Collapse
                in={isOpen}
                key={`level-${depth}-${child.id}`}
                className={`level-${depth + 1}`}
              >
                <MenuItemComponent
                  item={child}
                  onClickItemHandler={onClickItemHandler}
                  depth={depth + 1}
                  variant={variant}
                  activeItemId={activeItemId}
                  openedItemsIds={openedItemsIds}
                  customMenuItemComponent={customMenuItemComponent}
                  {...props}
                />
              </Collapse>
            ))}
        </Suspense>
      )}
    </StyledItemWrapper>
  );
};

const StyledMenuItem = styled("span")(
  ({ theme, isExpanded }: { theme: Theme; isExpanded: boolean }) => ({
    display: "flex",
    justifyContent: "space-between",
    padding: "0.75rem 1.5rem",
    fontWeight: theme.typography.fontWeightRegular,
    cursor: "pointer",
    textAlign: "left",
    maxWidth: "100%",
    fontSize: "14px",
    ".icon-collapse": {
      color: theme.tokens.colors.gray[6],
      width: "1rem",
      height: "1rem",
      display: "flex",
      alignSelf: "center",
      alignItems: "center",
      justifyContent: "center",
      svg: {
        width: "1rem",
        height: "1rem",
        verticalAlign: "middle",
        display: "block",
        transform: "scale(1.5)",
      },
    },
    "&.level-0": {
      fontWeight: theme.typography.fontWeightBold,
      fontFamily: "'Nunito', sans-serif",
    },

    "&.level-1": {
      backgroundColor: theme.tokens.colors.gray[1],
      fontWeight: theme.typography.fontWeightRegular,
      borderBottom: isExpanded
        ? `1px ${theme.tokens.colors.gray[4]} solid`
        : "none",
    },

    "&.level-2, &.level-3": {
      backgroundColor: theme.tokens.colors.gray[3],
    },
    "&.level-4": {
      backgroundColor: theme.tokens.colors.gray[4],
    },
    "&:hover": {
      background: theme.tokens.colors.gray[2],
      ">svg": {
        color: theme.tokens.colors.gray[7],
      },
    },
    "&.active": {
      backgroundColor: theme.tokens.colors.blue[4],
    },
  })
);
const StyledItemWrapper = styled("li")(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  textAlign: "center",
  ".expanded": {
    marginTop: "1rem",
  },
  "&.level-0": {
    borderBottom: `1px ${theme.tokens.colors.gray[4]} solid`,
    fontFamily: "'Nunito', sans-serif",
  },
}));
